import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { TriangleDiagram } from "@/components/tools/linear-algebra/triangle-diagram";
import type { TRIANGLE_SOLVER_STRINGS } from "@/components/tools/triangle-solver";
import { t } from "@/lib/i18n/interpolate";
import type { AngleUnit } from "@/lib/tool-kit/linear-algebra/angle-input";
import {
  type Ambiguity,
  propertiesOf,
  type Solution,
  type Triangle,
} from "@/lib/tool-kit/linear-algebra/triangle";

type Strings = typeof TRIANGLE_SOLVER_STRINGS;

export interface TriangleCtx {
  strings: Strings;
  unit: AngleUnit;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  givenSides: [boolean, boolean, boolean];
  givenAngles: [boolean, boolean, boolean];
}

const SIDES = ["a", "b", "c"] as const;
const ANGLES = ["A", "B", "C"] as const;

const num = (ctx: TriangleCtx, x: number) =>
  ctx.fmt(x, { maximumFractionDigits: 4 });

function angle(ctx: TriangleCtx, radians: number): string {
  if (ctx.unit === "radians") {
    return t(ctx.strings.radiansUnit, {
      value: ctx.fmt(radians, { maximumFractionDigits: 6 }),
    });
  }
  return t(ctx.strings.degreesUnit, {
    value: ctx.fmt((radians * 180) / Math.PI, { maximumFractionDigits: 4 }),
  });
}

function sumLine(
  ctx: TriangleCtx,
  tri: Triangle,
  k: number,
  primed = -1
): string {
  const others = [0, 1, 2].filter((i) => i !== k);
  const letter = (i: number) => (i === primed ? `${ANGLES[i]}′` : ANGLES[i]);
  return t(ctx.strings.angleSumLine, {
    k: letter(k),
    i: letter(others[0]),
    j: letter(others[1]),
    total: ctx.unit === "radians" ? "π" : "180°",
    result: angle(ctx, tri.angles[k]),
  });
}

/** Law of cosines solved for angle k, with this triangle's numbers. */
function cosineAngleLine(ctx: TriangleCtx, tri: Triangle, k: number): string {
  const [i, j] = [0, 1, 2].filter((x) => x !== k);
  const s = tri.sides;
  return t(ctx.strings.cosineAngleLine, {
    K: ANGLES[k],
    k: SIDES[k],
    i: SIDES[i],
    j: SIDES[j],
    ki: num(ctx, s[i]),
    kj: num(ctx, s[j]),
    kk: num(ctx, s[k]),
    cos: ctx.fmt((s[i] ** 2 + s[j] ** 2 - s[k] ** 2) / (2 * s[i] * s[j]), {
      maximumFractionDigits: 6,
    }),
    result: angle(ctx, tri.angles[k]),
  });
}

function cosineSideLine(ctx: TriangleCtx, tri: Triangle, k: number): string {
  const [i, j] = [0, 1, 2].filter((x) => x !== k);
  const s = tri.sides;
  return t(ctx.strings.cosineSideLine, {
    k: SIDES[k],
    K: ANGLES[k],
    i: SIDES[i],
    j: SIDES[j],
    si: num(ctx, s[i]),
    sj: num(ctx, s[j]),
    angle: angle(ctx, tri.angles[k]),
    sum: num(ctx, s[k] ** 2),
    result: num(ctx, s[k]),
  });
}

function sineSideLine(
  ctx: TriangleCtx,
  tri: Triangle,
  from: number,
  to: number
): string {
  return t(ctx.strings.sineSideLine, {
    to: SIDES[to],
    from: SIDES[from],
    sFrom: num(ctx, tri.sides[from]),
    To: ANGLES[to],
    From: ANGLES[from],
    sinTo: ctx.fmt(Math.sin(tri.angles[to]), { maximumFractionDigits: 6 }),
    sinFrom: ctx.fmt(Math.sin(tri.angles[from]), { maximumFractionDigits: 6 }),
    result: num(ctx, tri.sides[to]),
  });
}

function workingLines(
  solution: Solution,
  tri: Triangle,
  index: number,
  ctx: TriangleCtx
): string[] {
  const sideIdx = [0, 1, 2].filter((i) => ctx.givenSides[i]);
  const angleIdx = [0, 1, 2].filter((i) => ctx.givenAngles[i]);
  switch (solution.case) {
    case "SSS":
      return [
        cosineAngleLine(ctx, tri, 0),
        cosineAngleLine(ctx, tri, 1),
        sumLine(ctx, tri, 2),
      ];
    case "SAS": {
      const k = angleIdx[0];
      const j = sideIdx[1];
      const i = sideIdx[0];
      return [
        cosineSideLine(ctx, tri, k),
        cosineAngleLine(ctx, tri, j),
        sumLine(ctx, tri, i),
      ];
    }
    case "ASA":
    case "AAS": {
      const m = sideIdx[0];
      const k = 3 - angleIdx[0] - angleIdx[1];
      const unknownSides = [0, 1, 2].filter((i) => i !== m);
      return [
        sumLine(ctx, tri, k),
        ...unknownSides.map((to) => sineSideLine(ctx, tri, m, to)),
      ];
    }
    default: {
      const amb = solution.ambiguity as Ambiguity;
      const x = amb.angleIndex;
      const y = amb.otherIndex;
      const z = 3 - x - y;
      const secondary = index === 1;
      const two = amb.kind === "two";
      const firstKey = two ? ctx.strings.ssaFirst : ctx.strings.ssaOnly;
      return [
        t(ctx.strings.ssaSineLine, {
          Y: ANGLES[y],
          y: SIDES[y],
          x: SIDES[x],
          X: ANGLES[x],
          sy: num(ctx, tri.sides[y]),
          sx: num(ctx, tri.sides[x]),
          sinX: ctx.fmt(Math.sin(tri.angles[x]), { maximumFractionDigits: 6 }),
          sinY: ctx.fmt(Math.sin(tri.angles[y]), { maximumFractionDigits: 6 }),
        }),
        secondary
          ? t(ctx.strings.ssaSecond, {
              Y: ANGLES[y],
              result: angle(ctx, tri.angles[y]),
            })
          : t(firstKey, {
              Y: ANGLES[y],
              result: angle(ctx, tri.angles[y]),
            }),
        sumLine(ctx, tri, z, secondary ? y : -1),
        sineSideLine(ctx, tri, x, z),
      ];
    }
  }
}

function AmbiguityNote({
  solution,
  ctx,
}: {
  solution: Solution;
  ctx: TriangleCtx;
}) {
  const amb = solution.ambiguity;
  if (amb === null) {
    return null;
  }
  const { strings } = ctx;
  return (
    <div className={`${RESULT_CARD} space-y-2 text-sm`}>
      <p className="font-medium text-foreground">{strings.ssaHeading}</p>
      <p className="font-mono text-muted-foreground">
        {t(strings.ssaHeight, {
          y: SIDES[amb.otherIndex],
          X: ANGLES[amb.angleIndex],
          value: num(ctx, amb.height),
          x: SIDES[amb.oppositeIndex],
        })}
      </p>
      <p className="text-muted-foreground">
        {strings.ambiguityKinds[amb.kind]}
      </p>
    </div>
  );
}

function SingleTriangle({
  solution,
  tri,
  index,
  total,
  ctx,
}: {
  solution: Solution;
  tri: Triangle;
  index: number;
  total: number;
  ctx: TriangleCtx;
}) {
  const { strings } = ctx;
  const props = propertiesOf(tri);
  const sideTexts = SIDES.map(
    (name, i) => `${name} = ${num(ctx, tri.sides[i])}`
  ) as [string, string, string];
  const angleTexts = ANGLES.map((_, i) => angle(ctx, tri.angles[i])) as [
    string,
    string,
    string,
  ];
  const copy = [
    ...sideTexts,
    ...ANGLES.map((name, i) => `${name} = ${angleTexts[i]}`),
  ].join("\n");
  return (
    <section className="space-y-4">
      {total > 1 ? (
        <h3 className={WIDGET_H3}>
          {t(strings.triangleN, { n: ctx.fmt(index + 1) })}
        </h3>
      ) : null}
      <StatGrid>
        {SIDES.map((name, i) => (
          <StatCard
            emphasis={!ctx.givenSides[i]}
            key={name}
            label={t(strings.sideLabel, { name })}
            value={num(ctx, tri.sides[i])}
          />
        ))}
        {ANGLES.map((name, i) => (
          <StatCard
            emphasis={!ctx.givenAngles[i]}
            key={name}
            label={t(strings.angleLabel, { name })}
            value={angleTexts[i]}
          />
        ))}
        <StatCard label={strings.areaLabel} value={num(ctx, props.area)} />
        <StatCard
          label={strings.perimeterLabel}
          value={num(ctx, props.perimeter)}
        />
        <StatCard
          label={strings.circumradiusLabel}
          value={num(ctx, props.circumradius)}
        />
        <StatCard
          label={strings.inradiusLabel}
          value={num(ctx, props.inradius)}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {t(strings.classification, {
          bySides: strings.bySides[props.bySides],
          byAngles: strings.byAngles[props.byAngles],
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResults}
        small
        text={copy}
      />
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        {workingLines(solution, tri, index, ctx).map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>{t(strings.areaLine, { value: num(ctx, props.area) })}</p>
      </div>
      <TriangleDiagram
        downloadLabel={strings.downloadPng}
        fileName={`triangle-${index + 1}.png`}
        labels={{
          sides: sideTexts,
          angles: angleTexts,
          givenSides: ctx.givenSides,
          givenAngles: ctx.givenAngles,
        }}
        title={strings.diagramTitle}
        triangle={tri}
      />
    </section>
  );
}

export function TriangleResults({
  solution,
  ctx,
}: {
  solution: Solution;
  ctx: TriangleCtx;
}) {
  const { strings } = ctx;
  return (
    <>
      <p className="font-medium text-foreground text-sm">
        {t(strings.caseLine, {
          case: solution.case,
          description: strings.caseNames[solution.case],
        })}
      </p>
      <AmbiguityNote ctx={ctx} solution={solution} />
      {solution.triangles.length === 0 ? (
        <Notice>{strings.noTriangle}</Notice>
      ) : (
        <>
          <p className="text-muted-foreground text-xs">{strings.colourKey}</p>
          {solution.triangles.map((tri, index) => (
            <SingleTriangle
              ctx={ctx}
              index={index}
              key={tri.angles.join(",")}
              solution={solution}
              total={solution.triangles.length}
              tri={tri}
            />
          ))}
        </>
      )}
    </>
  );
}
